import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import "./CompanyTimeline.css";

/* ============================================================
   CONFIGURATION

   Dates accept:
     "2024"
     "2024-06"
     "2024-06-15"
     ISO timestamps

   Years/months resolve to the beginning of that period, in UTC.

   The example entries below are deliberately commented out:
   add your actual company dates rather than publishing invented
   founding dates or announcements.
   ============================================================ */

export const COMPANY_MILESTONES = [
  {
    id: "company-founded",
    title: "TayTanium Software founded",
    date: "2026-05-20",
    color: "#c9d4e0",
    category: "Company",
    description:
      "The beginning of TayTanium Software. Trying to find a domain name for the PVP website turned into something much bigger.",
  },
  /*{
    id: "next-announcement",
    title: "Next announcement",
    date: "2027-03-01",
    color: "#185e29",
    category: "Announcement",
    description:
      "A planned announcement. Add whatever details are public.",
    href: "/about",
    linkLabel: "Learn more",
  },*/
];

export const COMPANY_RANGES = [
  /*{
    id: "platform-rebuild",
    title: "Platform rebuild",
    time: [
      { start: "2025-02", end: "2025-09" },
      { start: "2026-03" }, // no end = to NOW
    ],
    color: "#71d9cf",
    category: "Infrastructure",
    description: "A focused period of work on the shared platform.",
  },
  {
    id: "legacy-format",
    title: "Still works",
    start: "2024-06",
    end: "2025-01",
  },*/
];

/* ============================================================
   CONSTANTS
   ============================================================ */

const DAY = 86_400_000;
const AXIS_HEIGHT = 44;
const RANGE_ROW_HEIGHT = 56;
const MILESTONE_CARD_HEIGHT = 52;
const MILESTONE_ROW_HEIGHT = 64;
const MILESTONE_TOP = 38;
const DEFAULT_COLOR = "#c9d4e0";

const dateFormatter = new Intl.DateTimeFormat("en", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

const monthFormatter = new Intl.DateTimeFormat("en", {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

/* ============================================================
   DATE UTILITIES
   ============================================================ */

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function parseDate(value) {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isFinite(time) ? time : null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const text = String(value).trim();
  const match = /^(\d{4})(?:-(\d{2}))?(?:-(\d{2}))?$/.exec(text);

  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2] ?? 1);
    const day = Number(match[3] ?? 1);

    const date = new Date(0);
    date.setUTCFullYear(year, month - 1, day);
    date.setUTCHours(0, 0, 0, 0);

    // Reject impossible dates instead of allowing Date to roll them over.
    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day
    ) {
      return null;
    }

    return date.getTime();
  }

  const time = Date.parse(text);
  return Number.isFinite(time) ? time : null;
}

function yearStart(year) {
  const date = new Date(0);
  date.setUTCFullYear(year, 0, 1);
  date.setUTCHours(0, 0, 0, 0);
  return date.getTime();
}

function getYear(time) {
  return new Date(time).getUTCFullYear();
}

function formatDate(time, originalValue) {
  const original = String(originalValue ?? "");

  if (/^\d{4}$/.test(original)) {
    return original;
  }

  if (/^\d{4}-\d{2}$/.test(original)) {
    return monthFormatter.format(time);
  }

  return dateFormatter.format(time);
}

function segmentIsActive(segment, now) {
  return segment.start <= now && (segment.end == null || segment.end > now);
}

function getState(item, now) {
  if (item.kind === "milestone") {
    return item.start > now ? "Upcoming" : "Recorded";
  }

  const segments = item.segments ?? [item];

  if (segments.some((s) => segmentIsActive(s, now))) return "Active";
  if (segments.every((s) => s.start > now)) return "Planned";
  // Some periods are done and another is scheduled later.
  if (segments.some((s) => s.start > now)) return "Paused";
  return "Completed";
}

function formatSegment(segment, now = Date.now()) {
  const start = formatDate(segment.start, segment.startValue);

  if (segment.end == null) {
    return segment.start > now ? `${start} — end TBD` : `${start} — Present`;
  }

  const end = formatDate(segment.end, segment.endValue);
  return end === start ? start : `${start} — ${end}`;
}

function getPeriod(item, now = Date.now()) {
  if (item.kind === "milestone") {
    return formatDate(item.start, item.startValue);
  }

  return (item.segments ?? [item])
    .map((segment) => formatSegment(segment, now))
    .join(", ");
}

/* ============================================================
   HOOKS
   ============================================================ */

function useLiveNow() {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const update = () => setNow(Date.now());

    // At a multi-year scale, 30 seconds is already subpixel precision.
    const interval = window.setInterval(update, 30_000);

    const onVisibility = () => {
      if (!document.hidden) update();
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", update);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", update);
    };
  }, []);

  return now;
}

function useElementWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(900);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = () => {
      const next = element.getBoundingClientRect().width;
      if (next > 0) setWidth(next);
    };

    measure();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return [ref, width];
}

/* ============================================================
   NORMALIZATION

   Keep the rendering layer independent of your product schema.
   Invalid entries are omitted.
   All IDs are namespaced to prevent project/milestone collisions.
   ============================================================ */

// Accepts the new `time: [{ start, end? }]` format, falling back to
// legacy single start/end keys. Invalid segments are dropped individually.
function readSegments(time, legacyStart, legacyEnd) {
  const raw =
    Array.isArray(time) && time.length
      ? time
      : legacyStart != null && legacyStart !== ""
        ? [{ start: legacyStart, end: legacyEnd }]
        : [];

  const segments = [];

  for (const range of raw) {
    if (!range) continue;

    const start = parseDate(range.start);
    const end = parseDate(range.end);

    if (start == null) continue;
    if (range.end && end == null) continue;
    if (end != null && end < start) continue;

    segments.push({
      start,
      end,
      startValue: range.start,
      endValue: range.end,
    });
  }

  return segments.sort((a, b) => a.start - b.start);
}

// Overall span, used for sorting, window sizing, and list grouping.
function spanOf(segments) {
  const first = segments[0];
  const ongoing = segments.some((s) => s.end == null);

  const last = ongoing
    ? null
    : segments.reduce((latest, s) => (s.end > latest.end ? s : latest));

  return {
    start: first.start,
    end: last ? last.end : null,
    startValue: first.startValue,
    endValue: last ? last.endValue : undefined,
  };
}

function normalizeEntries(products, milestones, ranges) {
  const result = [];

  for (const product of products) {
    const segments = readSegments(product.time, product.started, product.ended);
    if (!segments.length) continue;

    result.push({
      id: `project:${product.path || product.name}`,
      kind: "range",
      source: "Project",
      title: product.name,
      segments,
      ...spanOf(segments),
      color: product.accent || DEFAULT_COLOR,
      category: product.category || "Project",
      description: product.tagline || product.description || "",
      status: product.status,
      href: product.path,
      linkLabel: "Explore project",
    });
  }

  for (const range of ranges) {
    if (!range.id || !range.title) continue;

    const segments = readSegments(range.time, range.start, range.end);
    if (!segments.length) continue;

    result.push({
      id: `range:${range.id}`,
      kind: "range",
      source: "Timeframe",
      title: range.title,
      segments,
      ...spanOf(segments),
      color: range.color || DEFAULT_COLOR,
      category: range.category || "Initiative",
      description: range.description || "",
      status: range.status,
      href: range.href,
      linkLabel: range.linkLabel || "Learn more",
    });
  }

  for (const milestone of milestones) {
    const start = parseDate(milestone.date);

    if (!milestone.id || !milestone.title || start == null) continue;

    result.push({
      id: `milestone:${milestone.id}`,
      kind: "milestone",
      source: "Milestone",
      title: milestone.title,
      start,
      end: start,
      startValue: milestone.date,
      endValue: milestone.date,
      color: milestone.color || DEFAULT_COLOR,
      category: milestone.category || "Milestone",
      description: milestone.description || "",
      status: milestone.status,
      href: milestone.href,
      linkLabel: milestone.linkLabel || "Learn more",
    });
  }

  return result.sort(
    (a, b) => a.start - b.start || a.id.localeCompare(b.id),
  );
}


/* ============================================================
   WINDOW / SCALE

   Default:
     - includes every entry
     - includes the present
     - reserves room for the future
     - shows at least 8 years

   Optional startYear + years lock the displayed window.
   No horizontal scrolling is used.
   ============================================================ */

function calculateWindow(entries, now, startYear, years) {
  const currentYear = getYear(now);

  const firstYear = Math.min(
    currentYear - 2,
    ...entries.map((item) => getYear(item.start)),
  );

  const lastYear = Math.max(
    currentYear + 1,
    ...entries.map((item) =>
      getYear(Math.max(item.start, item.end ?? now)),
    ),
  );

  const first =
    Number.isInteger(startYear) ? startYear : firstYear;

  const neededYears = lastYear - first + 1;

  const count =
    Number.isInteger(years)
      ? Math.max(2, years)
      : Math.min(8, Math.max(2, neededYears));

  return {
    start: yearStart(first),
    end: yearStart(first + count),
    firstYear: first,
    years: count,
  };
}

function chooseYearStep(years, width) {
  const targetLabels = Math.max(2, Math.floor(width / 66));
  const needed = years / targetLabels;

  return [1, 2, 5, 10, 20, 50, 100].find(
    (step) => step >= needed,
  ) ?? Math.ceil(needed);
}

/* ============================================================
   RANGE PACKING

   Each range occupies an interval containing:
     1. its visible bar
     2. its start label
     3. a small separation gap

   Interval partitioning then assigns it to the first available
   lane. Disjoint projects share lanes. Labels never overlap
   another range assigned to the same lane.
   ============================================================ */

function packRanges(entries, scale, width, window, now) {
  const edge = 12;
  const compact = width < 560;
  const maxLabelWidth = compact ? 142 : 194;

  const candidates = entries
    .filter((entry) => entry.kind === "range")
    .flatMap((entry) =>
      entry.segments.map((segment, index) => {
        const effectiveEnd = Math.max(segment.start, segment.end ?? now);

        if (segment.start >= window.end || effectiveEnd < window.start) {
          return null;
        }

        const startX = scale(Math.max(segment.start, window.start));
        const realEndX = scale(Math.min(effectiveEnd, window.end));

        const endX = Math.min(width - edge, Math.max(startX + 8, realEndX));

        const labelWidth = Math.min(
          width - edge * 2,
          clamp(entry.title.length * 6.7 + 25, 96, maxLabelWidth),
        );

        const labelX = clamp(
          startX,
          edge,
          Math.max(edge, width - edge - labelWidth),
        );

        return {
          ...segment,
          id: `${entry.id}#${index}`,
          entryId: entry.id,
          kind: "range",
          title: entry.title,
          color: entry.color,
          segmentIndex: index,
          segmentCount: entry.segments.length,
          startX,
          endX,
          labelX,
          labelWidth,
          footprintStart: Math.min(startX - 4, labelX),
          footprintEnd: Math.max(endX + 4, labelX + labelWidth),
          futureStart: clamp(scale(now), startX, endX),
          clippedStart: segment.start < window.start,
          clippedEnd: effectiveEnd > window.end,
        };
      }),
    )
    .filter(Boolean)
    .sort(
      (a, b) =>
        a.footprintStart - b.footprintStart ||
        b.footprintEnd - a.footprintEnd,
    );

  const laneEnds = [];

  const items = candidates.map((item) => {
    let lane = laneEnds.findIndex((end) => item.footprintStart >= end + 12);

    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(item.footprintEnd);
    } else {
      laneEnds[lane] = item.footprintEnd;
    }

    return { ...item, lane };
  });

  return {
    items,
    lanes: laneEnds.length,
    height: Math.max(84, laneEnds.length * RANGE_ROW_HEIGHT + 18),
  };
}

/* ============================================================
   MILESTONE PACKING

   Milestone cards use at most maxMilestoneLanes tiers.

   If cards cannot fit:
     - merge the nearest chronological groups
     - try the layout again

   Clusters preserve every milestone. Clicking one opens a
   chooser in the detail panel, and the actual date pins remain
   on the axis.
   ============================================================ */

function layoutMilestoneGroups(groups, scale, width) {
  const cardWidth = Math.min(
    width - 8,
    width < 560 ? 120 : 156,
  );

  const laneEnds = [];

  const cards = groups.map((group) => {
    const anchor =
      group.items.reduce((sum, item) => sum + scale(item.start), 0) /
      group.items.length;

    const left = clamp(
      anchor - cardWidth / 2,
      4,
      width - cardWidth - 4,
    );

    let lane = laneEnds.findIndex(
      (end) => left >= end + 12,
    );

    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(left + cardWidth);
    } else {
      laneEnds[lane] = left + cardWidth;
    }

    return {
      ...group,
      anchor,
      left,
      width: cardWidth,
      lane,
      top: MILESTONE_TOP + lane * MILESTONE_ROW_HEIGHT,
      color:
        group.items.length === 1
          ? group.items[0].color
          : DEFAULT_COLOR,
    };
  });

  return { cards, lanes: laneEnds.length };
}

function packMilestones(
  entries,
  scale,
  width,
  window,
  maxMilestoneLanes,
) {
  let groups = entries
    .filter(
      (item) =>
        item.kind === "milestone" &&
        item.start >= window.start &&
        item.start < window.end,
    )
    .map((item) => ({
      id: item.id,
      items: [item],
    }));

  const allowedLanes = Math.max(
    1,
    Math.floor(maxMilestoneLanes),
  );

  let layout = layoutMilestoneGroups(groups, scale, width);

  while (layout.lanes > allowedLanes && groups.length > 1) {
    let closestIndex = 0;
    let closestDistance = Infinity;

    for (let index = 0; index < groups.length - 1; index += 1) {
      const left = groups[index].items;
      const right = groups[index + 1].items;

      const distance =
        right[0].start - left[left.length - 1].start;

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    }

    const merged = [
      ...groups[closestIndex].items,
      ...groups[closestIndex + 1].items,
    ];

    groups = [
      ...groups.slice(0, closestIndex),
      {
        id: merged.map((item) => item.id).join("|"),
        items: merged,
      },
      ...groups.slice(closestIndex + 2),
    ];

    layout = layoutMilestoneGroups(groups, scale, width);
  }

  return {
    ...layout,
    height:
      MILESTONE_TOP +
      Math.max(1, layout.lanes) * MILESTONE_ROW_HEIGHT +
      22,
  };
}

/* ============================================================
   ICONS
   ============================================================ */

function TimelineIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      aria-hidden="true"
    >
      <path d="M3 8h18M3 16h18M7 5v6M16 13v6" />
      <circle cx="7" cy="8" r="2" fill="currentColor" stroke="none" />
      <circle cx="16" cy="16" r="2" fill="currentColor" stroke="none" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M9 6h12M9 12h12M9 18h12M3 6h1M3 12h1M3 18h1" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 12h16m-6-6 6 6-6 6" />
    </svg>
  );
}

/* ============================================================
   SMALL UI COMPONENTS
   ============================================================ */

function StateBadge({ item, now }) {
  const state = getState(item, now);

  return (
    <span
      className="ct-state"
      data-state={state.toLowerCase()}
    >
      <span />
      {state}
    </span>
  );
}

function EntryLink({ item }) {
  if (!item.href) return null;

  const content = (
    <>
      {item.linkLabel}
      <ArrowIcon />
    </>
  );

  const isInternal =
    item.href.startsWith("/") && !item.href.startsWith("//");

  return isInternal ? (
    <Link className="ct-detail-link" to={item.href}>
      {content}
    </Link>
  ) : (
    <a className="ct-detail-link" href={item.href}>
      {content}
    </a>
  );
}

function DetailPanel({
  entries,
  now,
  panelId,
  onSelect,
  onClear,
}) {
  if (!entries.length) {
    return (
      <div
        id={panelId}
        className="ct-detail ct-detail-empty"
      >
        <span className="ct-detail-empty-icon" aria-hidden="true">
          <TimelineIcon />
        </span>

        <div>
          <h4>Every project has a story.</h4>
          <p>
            Select a milestone or a range to explore it.
            Overlapping milestone groups open here.
          </p>
        </div>
      </div>
    );
  }

  if (entries.length > 1) {
    return (
      <section
        id={panelId}
        className="ct-detail"
        aria-label="Selected milestone group"
      >
        <div className="ct-detail-topline">
          <span className="ct-kicker">
            {entries.length} milestones in this group
          </span>

          <button
            type="button"
            className="ct-close"
            onClick={onClear}
            aria-label="Clear timeline selection"
          >
            <svg
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  strokeWidth="1.8"
  strokeLinecap="round"
  aria-hidden="true"
>
  <path d="M6 6l12 12M18 6 6 18" />
</svg>
          </button>
        </div>

        <h4 className="ct-detail-title">A closer look</h4>

        <div className="ct-cluster-options">
          {entries.map((item) => (
            <button
              key={item.id}
              type="button"
              className="ct-cluster-option"
              style={{ "--entry-color": item.color }}
              onClick={() => onSelect([item.id])}
              aria-controls={panelId}
            >
              <span className="ct-entry-dot" />
              <span className="ct-cluster-option-copy">
                <strong>{item.title}</strong>
                <span>{getPeriod(item)}</span>
              </span>
              <ArrowIcon />
            </button>
          ))}
        </div>
      </section>
    );
  }

  const item = entries[0];

  return (
    <section
      id={panelId}
      className="ct-detail ct-detail-selected"
      style={{ "--entry-color": item.color }}
      aria-label={`Details for ${item.title}`}
    >
      <div className="ct-detail-topline">
        <span className="ct-kicker">
          <span className="ct-entry-dot" />
          {item.source}
          <span className="ct-meta-separator">/</span>
          {item.category}
        </span>

        <button
          type="button"
          className="ct-close"
          onClick={onClear}
          aria-label="Clear timeline selection"
        >
          <svg
  width="14"
  height="14"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  strokeWidth="1.8"
  strokeLinecap="round"
  aria-hidden="true"
>
  <path d="M6 6l12 12M18 6 6 18" />
</svg>
        </button>
      </div>

      <div className="ct-detail-content">
        <div className="ct-detail-copy">
          <h4 className="ct-detail-title">{item.title}</h4>

          <div className="ct-detail-meta">
            {item.kind === "range" ? (
              item.segments.map((segment, index) => (
                <span key={index} className="ct-detail-period">
                  {formatSegment(segment, now)}
                </span>
              ))
            ) : (
              <span className="ct-detail-period">{getPeriod(item, now)}</span>
            )}
            <StateBadge item={item} now={now} />
            {item.status && (
              <span className="ct-original-status">
                {item.status}
              </span>
            )}
          </div>

          <p className="ct-detail-description">
            {item.description || "More information will be added here."}
          </p>
        </div>

        <EntryLink item={item} />
      </div>
    </section>
  );
}

/* ============================================================
   TIMELINE VIEW
   ============================================================ */

function TimelineChart({
  entries,
  now,
  window,
  selectedIds,
  onSelect,
  onClear,
  panelId,
  maxMilestoneLanes,
}) {
  const [plotRef, width] = useElementWidth();
  const [hoveredMilestone, setHoveredMilestone] = useState(null);
  const [focusedMilestone, setFocusedMilestone] = useState(null);
  const [hoveredRange, setHoveredRange] = useState(null);
  const [focusedRange, setFocusedRange] = useState(null);

  // Hover and focus are tracked per *entry*, so every segment reacts together.
  function linkHandlers(entryId) {
    const clear = (current) => (current === entryId ? null : current);

    return {
      onPointerEnter: (event) => {
        if (event.pointerType !== "touch") setHoveredRange(entryId);
      },
      onPointerLeave: () => setHoveredRange(clear),
      onPointerCancel: () => setHoveredRange(clear),
      onFocus: () => setFocusedRange(entryId),
      onBlur: () => setFocusedRange(clear),
    };
  }
  const milestoneLift = 2;

  const geometry = useMemo(() => {
    const edge = 12;
    const usableWidth = Math.max(1, width - edge * 2);

    const scale = (time) =>
      edge +
      ((time - window.start) / (window.end - window.start)) *
        usableWidth;

    const ranges = packRanges(
      entries,
      scale,
      width,
      window,
      now,
    );

    const milestones = packMilestones(
      entries,
      scale,
      width,
      window,
      maxMilestoneLanes,
    );

    const step = chooseYearStep(window.years, width);
    const ticks = [];

    for (
      let year = window.firstYear;
      year < window.firstYear + window.years;
      year += 1
    ) {
      ticks.push({
        year,
        x: scale(yearStart(year)),
        major: (year - window.firstYear) % step === 0,
      });
    }

    return {
      scale,
      ranges,
      milestones,
      ticks,
      nowX: scale(now),
      height:
        milestones.height +
        AXIS_HEIGHT +
        ranges.height,
    };
  }, [entries, now, width, window, maxMilestoneLanes]);

  const {
    scale,
    ranges,
    milestones,
    ticks,
    nowX,
    height,
  } = geometry;

  const hasSelection = selectedIds.length > 0;
  const selected = new Set(selectedIds);

  const nowInWindow =
    now >= window.start && now <= window.end;

  const futureLeft = clamp(nowX, 0, width);

  const visibleCount =
  new Set(ranges.items.map((item) => item.entryId)).size +
  milestones.cards.reduce((sum, card) => sum + card.items.length, 0);

  function select(event, ids) {
    event.stopPropagation();
    onSelect(ids);
  }

  return (
    <div className="ct-chart-view">
      <div className="ct-chart-caption" aria-hidden="true">
        <span>Milestones</span>
      </div>

      <div className="ct-chart-padding">
        <div
          ref={plotRef}
          className="ct-plot"
          style={{ height }}
          onClick={onClear}
        >
          {/* Future shading covers milestones, axis, and ranges. */}
          {futureLeft < width && (
            <div
              className="ct-future-zone"
              style={{ left: futureLeft }}
              aria-hidden="true"
            />
          )}

          {/* Full-height year guides. */}
          <svg
            className="ct-year-grid"
            width={width}
            height={height}
            aria-hidden="true"
          >
            {ticks.map((tick) => (
              <line
                key={tick.year}
                x1={tick.x}
                x2={tick.x}
                y1="30"
                y2={height}
                className={
                  tick.major
                    ? "ct-year-guide"
                    : "ct-year-guide ct-year-guide-minor"
                }
              />
            ))}
          </svg>

          {/* One continuous NOW line. */}
          {nowInWindow && (
            <>
              <div
                className="ct-now-line"
                style={{ left: nowX }}
                aria-hidden="true"
              >
                <span
                  className="ct-now-axis-point"
                  style={{ top: milestones.height - 24 }}
                />
              </div>

              <div
                className="ct-now-label"
                style={{
                  left: clamp(nowX, 28, width - 28),
                }}
                aria-label="Current time"
              >
                <span />
                NOW
              </div>
            </>
          )}

          {/* Milestones above the central axis. */}
          <div
            className="ct-milestone-zone"
            style={{ height: milestones.height }}
          >
            <svg
              className="ct-milestone-connectors"
              width={width}
              height={milestones.height + 8}
              aria-hidden="true"
            >
              {milestones.cards.map((card) => {
                const active = card.items.some((item) =>
                  selected.has(item.id),
                );

                const dim = hasSelection && !active;
                const center = card.left + card.width / 2;
                const cardBottom =
                  card.top + MILESTONE_CARD_HEIGHT;

                const branchY = milestones.height - 9;
                const bendY = Math.min(
                  cardBottom + 16,
                  branchY,
                );

                const pins = card.items.map((item) =>
                  scale(item.start),
                );

                const firstPin = Math.min(...pins);
                const lastPin = Math.max(...pins);
                // Extend the diagonal upward beneath the card.
                // Hover lifts the card 2px, so 6px provides some extra coverage.
                const lifted =
                  hoveredMilestone === card.id ||
                  focusedMilestone === card.id;

                const extension = 6;

                function buildConnectorPath(lift) {
                  const cardEdgeY = cardBottom - lift;

                  // Extend upward along the diagonal, behind the card.
                  const diagonalHeight = Math.max(1, bendY - cardEdgeY);
                  const slopeX = (card.anchor - center) / diagonalHeight;

                  const extendedX = center - slopeX * extension;
                  const extendedY = cardEdgeY - extension;

                  return [
                    `M ${extendedX} ${extendedY}`,
                    `L ${card.anchor} ${bendY}`,
                    `L ${card.anchor} ${branchY}`,
                  ].join(" ");
                }

                const restingPath = buildConnectorPath(0);
                const liftedPath = buildConnectorPath(milestoneLift);
                return (
                  <g
                    key={card.id}
                    className="ct-milestone-leader"
                    data-dim={dim ? "true" : undefined}
                    data-selected={active ? "true" : undefined}
                    style={{ "--entry-color": card.color }}
                  >
                    {/* Diagonal first, then straight down. */}
                    <path
                      className="ct-milestone-moving-connector"
                      d={restingPath}
                      data-lifted={lifted ? "true" : undefined}
                      style={{
                        "--ct-resting-path": `path("${restingPath}")`,
                        "--ct-lifted-path": `path("${liftedPath}")`,
                      }}
                    />

                    {pins.length > 1 && (
                      <path
                        d={`M ${firstPin} ${branchY} H ${lastPin}`}
                      />
                    )}

                    {pins.map((pin, index) => (
                      <g key={card.items[index].id}>
                        <path
                          d={`M ${pin} ${branchY} V ${milestones.height}`}
                        />

                        <circle
                          className="ct-milestone-pin-halo"
                          cx={pin}
                          cy={milestones.height}
                          r={active ? 7 : 5}
                        />

                        <circle
                          className="ct-milestone-pin"
                          cx={pin}
                          cy={milestones.height}
                          r={active ? 3.5 : 2.5}
                        />
                      </g>
                    ))}
                  </g>
                );
              })}
            </svg>

            {milestones.cards.map((card) => {
              const active = card.items.some((item) =>
                selected.has(item.id),
              );

              const dim = hasSelection && !active;
              const clustered = card.items.length > 1;
              const first = card.items[0];

              const entirelyFuture = card.items.every(
                (item) => item.start > now,
              );

              const title = clustered
                ? `${card.items.length} milestones`
                : first.title;

              const subtitle = clustered
                ? `${formatDate(first.start, first.startValue)} + more`
                : formatDate(first.start, first.startValue);

              return (
                <button
                  key={card.id}
                  type="button"
                  className="ct-milestone-card"
                  style={{
                    "--entry-color": card.color,
                    "--ct-card-lift": `${milestoneLift}px`,
                    left: card.left,
                    top: card.top,
                    width: card.width,
                    height: MILESTONE_CARD_HEIGHT,
                  }}
                  onPointerEnter={(event) => {
                    if (event.pointerType !== "touch") {
                      setHoveredMilestone(card.id);
                    }
                  }}
                  onPointerLeave={() => setHoveredMilestone(null)}
                  onPointerCancel={() => setHoveredMilestone(null)}
                  onFocus={() => setFocusedMilestone(card.id)}
                  onBlur={() => setFocusedMilestone(null)}
                  data-lifted={
                    hoveredMilestone === card.id ||
                    focusedMilestone === card.id
                      ? "true"
                      : undefined
                  }
                  data-selected={active ? "true" : undefined}
                  data-dim={dim ? "true" : undefined}
                  data-future={entirelyFuture ? "true" : undefined}
                  aria-pressed={active}
                  aria-controls={panelId}
                  aria-label={
                    clustered
                      ? `Explore ${card.items.length} milestones: ${card.items
                          .map((item) => item.title)
                          .join(", ")}`
                      : `${first.title}, ${subtitle}${
                          entirelyFuture ? ", upcoming" : ""
                        }`
                  }
                  title={
                    clustered
                      ? card.items.map((item) => item.title).join("\n")
                      : first.title
                  }
                  onClick={(event) =>
                    select(
                      event,
                      card.items.map((item) => item.id),
                    )
                  }
                >
                  <span className="ct-milestone-card-top">
                    <span className="ct-entry-dot" />
                    <span className="ct-milestone-card-title">
                      {title}
                    </span>

                    {clustered && (
                      <span className="ct-cluster-plus" aria-hidden="true">
                        +
                      </span>
                    )}
                  </span>

                  <span className="ct-milestone-card-date">
                    {subtitle}
                  </span>
                </button>
              );
            })}

            {!milestones.cards.length && (
              <p className="ct-no-milestones">
                Company milestones will appear here.
              </p>
            )}
          </div>

          {/* Central axis. Only years are labeled. */}
          <div
            className="ct-axis"
            style={{
              top: milestones.height,
              height: AXIS_HEIGHT,
            }}
            aria-hidden="true"
          >
            <span className="ct-axis-baseline" />

            {ticks.map((tick) => (
              <span
                key={tick.year}
                className="ct-axis-tick"
                data-major={tick.major ? "true" : undefined}
                style={{ left: tick.x }}
              >
                <span className="ct-axis-tick-mark" />

                {tick.major && (
                  <span className="ct-axis-year">
                    {tick.year}
                  </span>
                )}
              </span>
            ))}
          </div>

          {/* Thin packed ranges below the axis. */}
          <div
            className="ct-range-zone"
            style={{
              top: milestones.height + AXIS_HEIGHT,
              height: ranges.height,
            }}
          >
            {ranges.items.map((item) => {
              const active = selected.has(item.entryId);
              const dim = hasSelection && !active;
              const linked =
                hoveredRange === item.entryId || focusedRange === item.entryId;

              const barWidth = Math.max(8, item.endX - item.startX);

              const futurePercent = clamp(
                ((item.futureStart - item.startX) / barWidth) * 100,
                0,
                100,
              );

              const rowTop = item.lane * RANGE_ROW_HEIGHT;
              const labelAttachX = clamp(
                item.startX - item.labelX,
                8,
                item.labelWidth - 8,
              );

              const period = formatSegment(item, now);
              const partLabel =
                item.segmentCount > 1
                  ? `, period ${item.segmentIndex + 1} of ${item.segmentCount}`
                  : "";
              const segmentState = getState({ kind: "range", segments: [item] }, now);

              const handlers = linkHandlers(item.entryId);

              return (
                <div
                  key={item.id}
                  className="ct-range-item"
                  data-selected={active ? "true" : undefined}
                  data-dim={dim ? "true" : undefined}
                  data-linked={linked ? "true" : undefined}
                  style={{ "--entry-color": item.color, top: rowTop }}
                >
                  <button
                    type="button"
                    className="ct-range-hit"
                    style={{ left: item.startX, width: Math.max(24, barWidth) }}
                    aria-label={`${item.title}, ${period}${partLabel}, ${segmentState}`}
                    aria-pressed={active}
                    aria-controls={panelId}
                    title={`${item.title} · ${period}`}
                    onClick={(event) => select(event, [item.entryId])}
                    {...handlers}
                  >
                    <span
                      className="ct-range-bar"
                      style={{ width: barWidth }}
                      data-clipped-start={item.clippedStart ? "true" : undefined}
                      data-clipped-end={item.clippedEnd ? "true" : undefined}
                    >
                      {futurePercent < 100 && (
                        <span
                          className="ct-range-bar-future"
                          style={{ left: `${futurePercent}%` }}
                        />
                      )}
                    </span>

                    <span className="ct-range-start-point" />

                    {item.end == null && item.start <= now && (
                      <span
                        className="ct-range-live-point"
                        style={{ left: barWidth }}
                      />
                    )}
                  </button>

                  <svg
                    className="ct-range-label-leader"
                    width={width}
                    height={RANGE_ROW_HEIGHT}
                    aria-hidden="true"
                  >
                    <path
                      transform="translate(2.3 0)"
                      d={[
                        `M ${item.startX} 13`,
                        `V 24`,
                        `L ${item.labelX + labelAttachX + 1} 30`,
                      ].join(" ")}
                    />
                  </svg>

                  <button
                    type="button"
                    className="ct-range-label"
                    style={{ left: item.labelX, width: item.labelWidth }}
                    aria-pressed={active}
                    aria-controls={panelId}
                    aria-label={`${item.title}${partLabel}`}
                    title={`${item.title} · ${period}`}
                    onClick={(event) => select(event, [item.entryId])}
                    {...handlers}
                  >
                    <span className="ct-range-label-name">{item.title}</span>

                    {item.start > now && (
                      <span className="ct-range-planned" aria-hidden="true">↗</span>
                    )}
                  </button>
                </div>
              );
            })}

            {!ranges.items.length && (
              <p className="ct-no-ranges">
                No ranges in this window.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="ct-chart-footer">
        <div className="ct-legend">
          <span>
            <i className="ct-legend-solid" />
            Recorded
          </span>
          <span>
            <i className="ct-legend-hatch" />
            Future
          </span>
        </div>

        <span className="ct-chart-hint">
          {entries.length > visibleCount
            ? `${entries.length - visibleCount} outside this window · all visible in list mode`
            : "Select to explore · click empty space to reset"}
        </span>
      </div>
    </div>
  );
}

/* ============================================================
   LIST VIEW

   Shares selection with timeline view.
   Long lists scroll inside the component instead of extending
   the About page indefinitely.
   ============================================================ */

function TimelineList({
  entries,
  now,
  selectedIds,
  onSelect,
  panelId,
}) {
  const selected = new Set(selectedIds);
  const hasSelection = selectedIds.length > 0;

  const groups = useMemo(() => {
    const byYear = new Map();

    for (const item of entries) {
      const year = getYear(item.start);

      if (!byYear.has(year)) {
        byYear.set(year, []);
      }

      byYear.get(year).push(item);
    }

    return Array.from(byYear.entries()).sort(
      ([yearA], [yearB]) => yearA - yearB,
    );
  }, [entries]);

  if (!entries.length) {
    return (
      <div className="ct-list-empty">
        Add project dates, milestones, or custom ranges to get started.
      </div>
    );
  }

  return (
    <div className="ct-list-view">
      <div className="ct-list-heading">
        <span>Chronological index</span>
        <span>{entries.length} entries</span>
      </div>

      <div
        className="ct-list-scroll"
        tabIndex={0}
        role="region"
        aria-label="Chronological timeline entries"
      >
        {groups.map(([year, items]) => (
          <section
            key={year}
            className="ct-list-year-group"
            aria-label={`${year} entries`}
          >
            <h4 className="ct-list-year">{year}</h4>

            <ul className="ct-list-items">
              {items.map((item) => {
                const active = selected.has(item.id);
                const future = item.start > now;

                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className="ct-list-entry"
                      style={{ "--entry-color": item.color }}
                      data-selected={active ? "true" : undefined}
                      data-dim={
                        hasSelection && !active ? "true" : undefined
                      }
                      data-future={future ? "true" : undefined}
                      aria-pressed={active}
                      aria-controls={panelId}
                      onClick={() => onSelect([item.id])}
                    >
                      <span
                        className={
                          item.kind === "milestone"
                            ? "ct-list-symbol ct-list-symbol-milestone"
                            : "ct-list-symbol ct-list-symbol-range"
                        }
                        aria-hidden="true"
                      />

                      <span className="ct-list-entry-copy">
                        <strong>{item.title}</strong>

                        <span className="ct-list-entry-meta">
                          <span>{item.source}</span>
                          <span aria-hidden="true">·</span>
                          <span>{getPeriod(item, now)}</span>
                        </span>
                      </span>

                      <StateBadge item={item} now={now} />
                      <ArrowIcon />
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   PUBLIC COMPONENT

   Props:
     products             Existing products array.
     milestones           Specific dates.
     ranges               Additional custom ranges.
     startYear            Optional fixed first year.
     years                Optional fixed window length.
     maxMilestoneLanes    Milestone tier limit before clustering.
     initialMode          "timeline" or "list".

   Default window automatically includes all entries and NOW,
   with at least eight years visible.
   ============================================================ */

const EMPTY_PRODUCTS = [];

export default function CompanyTimeline({
  products = EMPTY_PRODUCTS,
  milestones = COMPANY_MILESTONES,
  ranges = COMPANY_RANGES,
  startYear,
  years,
  maxMilestoneLanes = 2,
  initialMode = "timeline",
}) {
  const now = useLiveNow();
  const instanceId = useId();

  const titleId = `${instanceId}-title`;
  const panelId = `${instanceId}-details`;
  const viewId = `${instanceId}-view`;

  const [mode, setMode] = useState(
    initialMode === "list" ? "list" : "timeline",
  );

  const [selectedIds, setSelectedIds] = useState([]);

  const entries = useMemo(
    () => normalizeEntries(products, milestones, ranges),
    [products, milestones, ranges],
  );

  const timelineWindow = useMemo(
    () => calculateWindow(entries, now, startYear, years),
    [entries, now, startYear, years],
  );

  const entryMap = useMemo(
    () => new Map(entries.map((item) => [item.id, item])),
    [entries],
  );

  const selectedEntries = selectedIds
    .map((id) => entryMap.get(id))
    .filter(Boolean);

  // Prevent stale selection when configuration changes.
  useEffect(() => {
    setSelectedIds((current) => {
      const next = current.filter((id) => entryMap.has(id));

      return next.length === current.length ? current : next;
    });
  }, [entryMap]);

  function handleSelect(ids) {
    setSelectedIds((current) => {
      const same =
        current.length === ids.length &&
        current.every((id, index) => id === ids[index]);

      return same ? [] : ids;
    });
  }

  function clearSelection() {
    setSelectedIds((current) => (current.length ? [] : current));
  }

  const projectCount = entries.filter(
    (item) => item.source === "Project",
  ).length;

  const milestoneCount = entries.filter(
    (item) => item.kind === "milestone",
  ).length;

  const extraRangeCount = entries.filter(
    (item) => item.source === "Timeframe",
  ).length;

  const announcement = selectedEntries.length
    ? selectedEntries.length === 1
      ? `Selected ${selectedEntries[0].title}. Details are below the timeline.`
      : `Selected a group of ${selectedEntries.length} milestones. Choose one in the details panel below.`
    : "No timeline entry selected.";

  return (
    <section
      className="company-timeline"
      aria-labelledby={titleId}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          clearSelection();
        }
      }}
    >
      <header className="ct-header">
        <div className="ct-header-copy">
          <p className="eyebrow eyebrow-accent">
            {/* <span className="ct-system-dot" /> */}
            The TayTanium timeline
          </p>

          <h2 id={titleId}>When we build</h2>

          <p className="ct-header-description">
            The projects, turning points, and ideas still ahead.
          </p>
        </div>

        <div
          className="ct-mode-toggle"
          role="group"
          aria-label="Timeline display mode"
        >
          <button
            type="button"
            aria-pressed={mode === "timeline"}
            aria-controls={viewId}
            onClick={() => setMode("timeline")}
          >
            <TimelineIcon />
            <span>Timeline</span>
          </button>

          <button
            type="button"
            aria-pressed={mode === "list"}
            aria-controls={viewId}
            onClick={() => setMode("list")}
          >
            <ListIcon />
            <span>List</span>
          </button>
        </div>
      </header>

      <div className="ct-shell">
        <div className="ct-toolbar">
          <div className="ct-toolbar-counts">
            <span>
              <strong>{projectCount}</strong> projects
            </span>
            <span>
              <strong>{milestoneCount}</strong> milestones
            </span>
          </div>

          <div className="ct-toolbar-right">
            {selectedEntries.length > 0 && (
              <button
                type="button"
                className="ct-reset"
                onClick={clearSelection}
              >
                Clear selection
                <span aria-hidden="true">×</span>
              </button>
            )}

          </div>
        </div>

        <div id={viewId} className="ct-view" key={mode}>
          {mode === "timeline" ? (
            <TimelineChart
              entries={entries}
              now={now}
              window={timelineWindow}
              selectedIds={selectedIds}
              onSelect={handleSelect}
              onClear={clearSelection}
              panelId={panelId}
              maxMilestoneLanes={maxMilestoneLanes}
            />
          ) : (
            <TimelineList
              entries={entries}
              now={now}
              selectedIds={selectedIds}
              onSelect={handleSelect}
              panelId={panelId}
            />
          )}
        </div>

        <div className="ct-detail-wrap">
          <DetailPanel
            key={selectedEntries.map((item) => item.id).join("|") || "empty"}
            entries={selectedEntries}
            now={now}
            panelId={panelId}
            onSelect={handleSelect}
            onClear={clearSelection}
          />
        </div>
      </div>

      <p className="ct-sr-only" role="status" aria-live="polite">
        {announcement}
      </p>
    </section>
  );
}